import {
  MAX_USER_CREDIT_CARDS,
  type BillingCycleHorizon,
  type BurnVelocityStatus,
  type CreditCardProfileDto,
  type EntitlementMode,
  type ExpenseCategory,
  type ExpenseImpactSimulation,
  type ExpenseLineItem,
  type ExpenseSource,
  type InflowFrequency,
  type PaymentMethodType,
  type ReceiptExtractionResult,
  type RecurringValidityMode,
  type SolTemporalKpiSnapshot,
  type SubscriptionStatus,
  type WindfallAllocationMode,
} from '@economi/domain-contracts';

export { MAX_USER_CREDIT_CARDS };
export type {
  BillingCycleHorizon,
  CreditCardProfileDto,
  PaymentMethodType,
  RecurringValidityMode,
};

export interface RecurringInflowItem {
  readonly id: string;
  readonly name: string;
  readonly amountCents: number;
  readonly amountPerOccurrenceCents?: number;
  readonly occurrencesPerCycle?: number;
  readonly frequency: InflowFrequency;
  readonly customIntervalDays?: number;
  readonly renewalDayOfMonth: number;
  readonly secondBillingDayOfMonth?: number;
  readonly isPrimaryCycleAnchor: boolean;
  readonly isActive?: boolean;
}

export interface RecurringExpenseItem {
  readonly id: string;
  readonly name: string;
  readonly merchantName: string;
  readonly issuerRuc: string | null;
  readonly category: ExpenseCategory;
  readonly frequency?: InflowFrequency;
  readonly customIntervalDays?: number;
  readonly defaultAmountCents: number;
  readonly billingDayOfMonth: number;
  readonly isInevitable: boolean;
  readonly validityMode?: RecurringValidityMode;
  readonly expirationDateIso?: string | null;
  readonly isCreditCardExpense?: boolean;
  readonly creditCardId?: string | null;
  readonly isActive: boolean;
  readonly monthlyOverridesCents: Readonly<Record<string, number>>;
}

export const INFLOW_FREQUENCY_LABELS: Record<InflowFrequency, string> = {
  MONTHLY: 'Mensual (1 vez al mes)',
  BIWEEKLY: 'Quincenal (Cada 15 días)',
  WEEKLY: 'Semanal (Cada 7 días)',
  CUSTOM_DAYS: 'Personalizado (Cada N días)',
};

export function getDefaultOccurrencesForFrequency(frequency: InflowFrequency): number {
  switch (frequency) {
    case 'MONTHLY':
      return 1;
    case 'BIWEEKLY':
      return 2;
    case 'WEEKLY':
      return 4;
    case 'CUSTOM_DAYS':
      return 1;
  }
}

export interface WindfallInflowItem {
  readonly id: string;
  readonly description: string;
  readonly amountCents: number;
  readonly allocationMode: WindfallAllocationMode;
  readonly customAllocationBp: number;
  readonly receivedAtIso: string;
  readonly capContributionCents: number;
  readonly protectedSavingsCents: number;
}

export interface ExpenseLedgerItem {
  readonly id: string;
  readonly merchantName: string;
  readonly issuerRuc?: string | null;
  readonly receiptNumber?: string | null;
  readonly description: string;
  readonly category: ExpenseCategory;
  readonly amountCents: number;
  readonly occurredAtIso: string;
  readonly source: ExpenseSource;
  readonly r2ReceiptKey?: string;
  readonly receiptImageDataUrl?: string | null;
  readonly recurringRuleId?: string;
  readonly isInevitable?: boolean;
  readonly paymentMethod?: PaymentMethodType;
  readonly creditCardId?: string | null;
  readonly cardLastFourDigits?: string | null;
  readonly billingCycleHorizon?: BillingCycleHorizon;
  readonly lineItems?: ReadonlyArray<ExpenseLineItem>;
  readonly dailyCompressionCents: number;
  readonly budgetHoursConsumed: number;
}

export function matchCreditCardByLastFour(
  creditCards: ReadonlyArray<CreditCardProfileDto> | undefined,
  cardLastFourDigits: string | null | undefined
): CreditCardProfileDto | null {
  const digits = (cardLastFourDigits ?? '').replace(/\D/g, '').slice(-4);
  if (digits.length !== 4 || !creditCards || creditCards.length === 0) {
    return null;
  }
  const activeMatch = creditCards.find(
    (card) =>
      card.isActive !== false &&
      (card.lastFourDigits ?? '').replace(/\D/g, '').slice(-4) === digits
  );
  if (activeMatch) {
    return activeMatch;
  }
  return (
    creditCards.find(
      (card) =>
        (card.lastFourDigits ?? '').replace(/\D/g, '').slice(-4) === digits
    ) ?? null
  );
}

export interface DeveloperApiKeyItem {
  readonly id: string;
  readonly label: string;
  readonly keyPrefix: string;
  readonly fullTokenPreview: string;
  readonly scopes: ReadonlyArray<string>;
  readonly createdAtIso: string;
  readonly lastUsedAtIso: string | null;
}

export interface PilotEmailStatus {
  readonly email: string;
  readonly roleLabel: string;
  readonly verifiedViaResend: boolean;
  readonly lastOtpCode: string;
  readonly verifiedAtIso: string;
}

export interface EntitlementState {
  readonly mode: EntitlementMode;
  readonly maxPilotEmails: 2;
  readonly pilotAllowlist: ReadonlyArray<PilotEmailStatus>;
  readonly trialDaysDuration: number;
  readonly monthlyPriceSoles: number;
  readonly activeUserEmail: string;
  readonly activeSubscriptionStatus: SubscriptionStatus;
}

export interface FinancialCycleState {
  readonly cycleId: string;
  readonly startDateIso: string;
  readonly referenceDateIso: string;
  readonly renewalDateIso: string;
  readonly spendingRateBp: number;
  readonly keepRateConstantAcrossPeriods: boolean;
  readonly creditCards?: ReadonlyArray<CreditCardProfileDto>;
  readonly recurringInflows: ReadonlyArray<RecurringInflowItem>;
  readonly recurringExpenses?: ReadonlyArray<RecurringExpenseItem>;
  readonly windfallInflows: ReadonlyArray<WindfallInflowItem>;
  readonly expenses: ReadonlyArray<ExpenseLedgerItem>;
  readonly previousCyclesHistory: ReadonlyArray<{
    readonly cycleLabel: string;
    readonly spendingRateBp: number;
    readonly totalCapCents: number;
    readonly totalSpentCents: number;
  }>;
}

export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  FOOD_DINING: 'Alimentación / Restaurantes',
  GROCERIES: 'Supermercado / Bodega',
  TRANSPORT: 'Transporte / Combustible',
  UTILITIES: 'Servicios (Luz, Agua, Fibra)',
  HOUSING: 'Vivienda / Alquiler',
  HEALTH: 'Salud / Farmacia',
  ENTERTAINMENT: 'Entretenimiento / Ocio',
  SHOPPING: 'Compras / Retail',
  EDUCATION: 'Educación / Libros',
  FINANCIAL: 'Comisiones / Seguros',
  OTHER: 'Otros Gastos',
};

export const EXPENSE_CATEGORY_COLORS: Record<ExpenseCategory, string> = {
  HOUSING: '#10b981',
  GROCERIES: '#06b6d4',
  UTILITIES: '#3b82f6',
  TRANSPORT: '#f59e0b',
  HEALTH: '#ec4899',
  FOOD_DINING: '#8b5cf6',
  SHOPPING: '#14b8a6',
  ENTERTAINMENT: '#f97316',
  EDUCATION: '#6366f1',
  FINANCIAL: '#64748b',
  OTHER: '#94a3b8',
};

export function getRecurringExpenseAmountForMonthCents(
  rule: RecurringExpenseItem,
  monthIso: string
): number {
  const overrideCents = rule.monthlyOverridesCents[monthIso];
  if (overrideCents !== undefined) {
    return overrideCents;
  }
  return rule.defaultAmountCents;
}

export function isRecurringExpenseApplicableInCycle(
  rule: RecurringExpenseItem,
  cycleStartDateIso: string
): boolean {
  if (rule.isActive === false) {
    return false;
  }
  if (
    rule.validityMode === 'EXPIRES_ON_DATE' &&
    rule.expirationDateIso &&
    rule.expirationDateIso.slice(0, 10) < cycleStartDateIso.slice(0, 10)
  ) {
    return false;
  }
  return true;
}

export function resolveCardBillingCloseDateForCycle(
  billingCloseDayOfMonth: number,
  cycleStartDateIso: string,
  cycleRenewalDateIso: string
): string {
  const startParts = cycleStartDateIso.slice(0, 10).split('-').map(Number);
  const startYear = startParts[0] || 2026;
  const startMonth = startParts[1] || 10;
  const startDay = startParts[2] || 1;
  const closeDay = Math.min(31, Math.max(1, Math.round(billingCloseDayOfMonth)));

  let targetYear = startYear;
  let targetMonth = startMonth;

  if (closeDay < startDay) {
    targetMonth += 1;
    if (targetMonth > 12) {
      targetMonth = 1;
      targetYear += 1;
    }
  }

  const daysInTargetMonth = new Date(
    Date.UTC(targetYear, targetMonth, 0)
  ).getUTCDate();
  const clampedDay = Math.min(closeDay, daysInTargetMonth);
  const candidateIso = `${String(targetYear).padStart(4, '0')}-${String(
    targetMonth
  ).padStart(2, '0')}-${String(clampedDay).padStart(2, '0')}`;

  const renewalDayIso = cycleRenewalDateIso.slice(0, 10);
  return candidateIso > renewalDayIso ? renewalDayIso : candidateIso;
}

export function resolveCardPaymentDueDateForCycle(
  paymentDueDayOfMonth: number,
  billingCloseDateIso: string
): string {
  const closeParts = billingCloseDateIso.slice(0, 10).split('-').map(Number);
  const closeYear = closeParts[0] || 2026;
  const closeMonth = closeParts[1] || 10;
  const closeDay = closeParts[2] || 1;
  const dueDay = Math.min(31, Math.max(1, Math.round(paymentDueDayOfMonth)));

  let targetYear = closeYear;
  let targetMonth = closeMonth;
  if (dueDay <= closeDay) {
    targetMonth += 1;
    if (targetMonth > 12) {
      targetMonth = 1;
      targetYear += 1;
    }
  }

  const daysInTargetMonth = new Date(
    Date.UTC(targetYear, targetMonth, 0)
  ).getUTCDate();
  const clampedDay = Math.min(dueDay, daysInTargetMonth);
  return `${String(targetYear).padStart(4, '0')}-${String(targetMonth).padStart(
    2,
    '0'
  )}-${String(clampedDay).padStart(2, '0')}`;
}

export function classifyExpenseHorizonForCardInCycle(
  state: Pick<
    FinancialCycleState,
    'startDateIso' | 'renewalDateIso' | 'creditCards'
  >,
  occurredAtIso: string,
  creditCardId?: string | null,
  explicitHorizon?: BillingCycleHorizon
): BillingCycleHorizon {
  if (explicitHorizon) {
    return explicitHorizon;
  }
  const cleanCardId = creditCardId?.trim();
  if (!cleanCardId) {
    return 'CURRENT_CYCLE';
  }
  const card = (state.creditCards ?? []).find((c) => c.id === cleanCardId);
  if (!card) {
    return 'CURRENT_CYCLE';
  }
  const closeDateIso = resolveCardBillingCloseDateForCycle(
    card.billingCloseDayOfMonth,
    state.startDateIso,
    state.renewalDateIso
  );
  const expDateIso = occurredAtIso.slice(0, 10);
  return expDateIso > closeDateIso ? 'NEXT_CYCLE' : 'CURRENT_CYCLE';
}

export function addOneMonthPreservingDayIso(
  dateIso: string,
  preferredDay?: number
): string {
  const clean = dateIso.slice(0, 10);
  const parts = clean.split('-').map(Number);
  const year = parts[0] || 2026;
  const month = parts[1] || 1;
  const day = preferredDay ?? parts[2] ?? 1;
  const nextMonthIndex = month;
  const targetYear = year + Math.floor(nextMonthIndex / 12);
  const targetMonth1Based = (nextMonthIndex % 12) + 1;
  const daysInTargetMonth = new Date(
    Date.UTC(targetYear, targetMonth1Based, 0)
  ).getUTCDate();
  const clampedDay = Math.min(Math.max(1, day), daysInTargetMonth);
  return `${targetYear}-${String(targetMonth1Based).padStart(2, '0')}-${String(
    clampedDay
  ).padStart(2, '0')}`;
}

const LEGACY_SAMPLE_RECEIPT_NUMBERS = new Set<string>([
  'B084-00491823',
  'B012-0019284',
  'YAPE-04918274',
  'T004-0891234',
  'B019-0091821',
  'S001-4491820',
  'T004-0812300',
  'B004-0192834',
  'B001-00045821',
  'YAPE-OP-984210',
]);

/**
 * Detecta si un gasto proviene de plantillas de demostración o data sembrada antigua
 * para purgarlo automáticamente y nunca ensuciar el listado del usuario.
 */
export function isSampleOrDemoExpense(exp: ExpenseLedgerItem): boolean {
  const id = (exp.id ?? '').trim();
  const merch = (exp.merchantName ?? '').trim();
  const recNum = (exp.receiptNumber ?? '').trim();
  const r2Key = (exp.r2ReceiptKey ?? '').trim();
  const recRuleId = (exp.recurringRuleId ?? '').trim();
  const cents = exp.amountCents ?? 0;

  if (
    id.startsWith('exp-seed') ||
    id.startsWith('exp-demo') ||
    id.startsWith('tpl-') ||
    recRuleId === 'rexp-alquiler' ||
    recRuleId === 'rexp-servicios' ||
    id.includes('rexp-alquiler') ||
    id.includes('rexp-servicios')
  ) {
    return true;
  }

  if (recNum && LEGACY_SAMPLE_RECEIPT_NUMBERS.has(recNum)) {
    return true;
  }

  if (
    r2Key.startsWith('r2://economi-receipts-prod/') ||
    r2Key.includes('0001_B084-00491823') ||
    r2Key.includes('0002_B012-0019284') ||
    r2Key.includes('0003_YAPE-04918274') ||
    r2Key.includes('0004_T004-0891234')
  ) {
    return true;
  }

  if (
    (id === 'exp-01' && merch.includes('Arrendamiento & Mantenimiento')) ||
    (id === 'exp-02' && merch.includes('Supermercados Wong')) ||
    (id === 'exp-03' && merch.includes('Luz del Sur & Internet Fibra')) ||
    (id === 'exp-04' && merch.includes('Estación Primax')) ||
    (id === 'exp-05' && merch.includes('Clínica Internacional')) ||
    (merch === 'Supermercados Peruanos S.A. (Plaza Vea)' && cents === 18650) ||
    (merch.includes('Cinco Millas S.A.C.') && cents === 11800) ||
    (merch === 'Pastelería San Antonio (Yape QR)' && cents === 4250) ||
    (merch.includes('Coesti S.A.') && cents === 14500) ||
    (merch === 'Plaza Vea - Supermercados Peruanos S.A.' && cents === 4890)
  ) {
    return true;
  }

  return false;
}

/**
 * Resuelve la URL o Data URL de la imagen de la boleta/recibo asociada a un gasto.
 * Devuelve `null` si el gasto fue registrado manualmente sin imagen.
 */
export function resolveExpenseReceiptImageUrl(
  exp: ExpenseLedgerItem
): string | null {
  const inlineDataUrl = exp.receiptImageDataUrl?.trim();
  if (
    inlineDataUrl &&
    (inlineDataUrl.startsWith('data:image/') ||
      inlineDataUrl.startsWith('http://') ||
      inlineDataUrl.startsWith('https://') ||
      inlineDataUrl.startsWith('/'))
  ) {
    return inlineDataUrl;
  }

  const r2Key = exp.r2ReceiptKey?.trim() ?? '';
  if (
    r2Key.length > 0 &&
    !r2Key.startsWith('r2://economi-receipts-prod/') &&
    !r2Key.includes('pending/')
  ) {
    return `/api/v1/receipts/image?key=${encodeURIComponent(
      r2Key
    )}&receiptNumber=${encodeURIComponent(
      exp.receiptNumber ?? ''
    )}&merchantName=${encodeURIComponent(exp.merchantName)}&amountCents=${
      exp.amountCents
    }`;
  }

  if (exp.source === 'RECEIPT_OCR') {
    return `/api/v1/receipts/image?key=&receiptNumber=${encodeURIComponent(
      exp.receiptNumber ?? ''
    )}&merchantName=${encodeURIComponent(exp.merchantName)}&amountCents=${
      exp.amountCents
    }`;
  }

  return null;
}

/**
 * Garantiza que TODOS los gastos recurrentes activos se descuenten de forma
 * automática e inmediata al inicio del ciclo (`startDateIso`), sin importar
 * su día referencial de pago (`billingDayOfMonth`), dado que son gastos
 * inevitables que siempre se pagarán en el período. Además limpia cualquier
 * registro de ejemplo o plantilla antigua.
 */
export function synchronizeCycleRecurringExpenses(
  rawState: FinancialCycleState
): FinancialCycleState {
  const state: FinancialCycleState = {
    ...rawState,
    recurringInflows: (rawState.recurringInflows ?? []).filter(
      (r) =>
        !(
          r.id === 'rec-sueldo-principal' &&
          r.name.includes('Planilla 5ta Categoría')
        )
    ),
    recurringExpenses: (rawState.recurringExpenses ?? []).filter(
      (r) => r.id !== 'rexp-alquiler' && r.id !== 'rexp-servicios'
    ),
    windfallInflows: (rawState.windfallInflows ?? []).filter(
      (w) =>
        w.id !== 'wf-bono-productividad' &&
        w.id !== 'wf-asesoria-extra' &&
        w.id !== 'wf-devolucion-sunat'
    ),
    expenses: (rawState.expenses ?? []).filter(
      (e) => !isSampleOrDemoExpense(e)
    ),
    previousCyclesHistory: (rawState.previousCyclesHistory ?? []).filter(
      (h) =>
        h.cycleLabel !== 'Agosto 2026' && h.cycleLabel !== 'Septiembre 2026'
    ),
  };

  const cycleStartIso = state.startDateIso.slice(0, 10);
  const cycleEndIso = state.renewalDateIso.slice(0, 10);
  const activeMonthIso = cycleStartIso.slice(0, 7);
  const rules = state.recurringExpenses ?? [];

  const totalCycleDays = Math.max(
    1,
    diffDaysBetweenIso(state.startDateIso, state.renewalDateIso)
  );
  const rawElapsed = diffDaysBetweenIso(
    state.startDateIso,
    state.referenceDateIso
  );
  const elapsedDays = Math.max(0, Math.min(totalCycleDays, rawElapsed));
  const remainingDays = Math.max(1, totalCycleDays - elapsedDays);

  const recurringInflowCents = state.recurringInflows.reduce(
    (acc, item) => acc + (item.isActive === false ? 0 : item.amountCents),
    0
  );
  const recurringCapContributionCents = Math.round(
    (recurringInflowCents * state.spendingRateBp) / 10000
  );
  let windfallCapContributionCents = 0;
  for (const wf of state.windfallInflows) {
    windfallCapContributionCents += computeWindfallContributionCents(
      wf.amountCents,
      wf.allocationMode,
      state.spendingRateBp,
      wf.customAllocationBp
    ).capContributionCents;
  }
  const effectiveSpendingCapCents =
    recurringCapContributionCents + windfallCapContributionCents;
  const baselineDailyAllowanceCents = Math.max(
    1,
    Math.round(effectiveSpendingCapCents / totalCycleDays)
  );

  // Conservamos todos los gastos no recurrentes y los gastos recurrentes de períodos pasados cerrados
  const preservedExpenses = state.expenses
    .filter((exp) => {
      const isRecurringEntry =
        exp.source === 'RECURRING_RULE' || Boolean(exp.recurringRuleId);
      if (!isRecurringEntry) {
        return true;
      }
      const expDate = exp.occurredAtIso.slice(0, 10);
      const belongsToCurrentCycleRange = isDateWithinPeriod(
        expDate,
        cycleStartIso,
        cycleEndIso
      );
      const belongsToCurrentStartMonth =
        expDate.slice(0, 7) === activeMonthIso ||
        exp.receiptNumber === `REC-${activeMonthIso}` ||
        exp.id.endsWith(`-${cycleStartIso}`);

      // Si pertenece al ciclo/mes activo, se regenera abajo con fecha exacta `cycleStartIso`
      return !belongsToCurrentCycleRange && !belongsToCurrentStartMonth;
    })
    .map((exp) => {
      const isRecurringEntry =
        exp.source === 'RECURRING_RULE' || Boolean(exp.recurringRuleId);
      if (isRecurringEntry) {
        return exp;
      }
      const resolvedHorizon = classifyExpenseHorizonForCardInCycle(
        state,
        exp.occurredAtIso,
        exp.creditCardId
      );
      if (exp.billingCycleHorizon === resolvedHorizon) {
        return exp;
      }
      return {
        ...exp,
        billingCycleHorizon: resolvedHorizon,
      };
    });

  const autoDeductedRecurringEntries: ExpenseLedgerItem[] = [];
  for (const rule of rules) {
    if (!isRecurringExpenseApplicableInCycle(rule, cycleStartIso)) continue;
    const effectiveCents = getRecurringExpenseAmountForMonthCents(
      rule,
      activeMonthIso
    );
    if (effectiveCents <= 0) continue;

    const dailyCompressionCents = Math.round(effectiveCents / remainingDays);
    const budgetHoursConsumed = Number(
      ((effectiveCents / baselineDailyAllowanceCents) * 24).toFixed(2)
    );
    const isCardExpense = Boolean(rule.isCreditCardExpense && rule.creditCardId);

    autoDeductedRecurringEntries.push({
      id: `exp-rec-${rule.id}-${cycleStartIso}`,
      merchantName: rule.merchantName || rule.name,
      issuerRuc: rule.issuerRuc ?? null,
      receiptNumber: `REC-${activeMonthIso}`,
      description: `Gasto recurrente (descuento automático al iniciar ciclo): ${rule.name}`,
      category: rule.category,
      amountCents: effectiveCents,
      occurredAtIso: cycleStartIso,
      source: 'RECURRING_RULE',
      recurringRuleId: rule.id,
      isInevitable: rule.isInevitable ?? true,
      paymentMethod: isCardExpense ? 'CREDIT_CARD' : 'DIRECT_DEBIT_OR_CASH',
      creditCardId: isCardExpense ? (rule.creditCardId ?? null) : null,
      billingCycleHorizon: 'CURRENT_CYCLE',
      lineItems: [
        {
          description: rule.name,
          quantity: 1,
          totalCents: effectiveCents,
        },
      ],
      dailyCompressionCents,
      budgetHoursConsumed,
    });
  }

  const nextExpenses = [...autoDeductedRecurringEntries, ...preservedExpenses];

  const isUnchanged =
    (state.creditCards ?? []).length === (rawState.creditCards ?? []).length &&
    state.recurringInflows.length === (rawState.recurringInflows ?? []).length &&
    (state.recurringExpenses ?? []).length ===
      (rawState.recurringExpenses ?? []).length &&
    state.windfallInflows.length === (rawState.windfallInflows ?? []).length &&
    state.previousCyclesHistory.length ===
      (rawState.previousCyclesHistory ?? []).length &&
    nextExpenses.length === (rawState.expenses ?? []).length &&
    nextExpenses.every((item, idx) => {
      const prev = rawState.expenses[idx];
      return (
        prev !== undefined &&
        prev.id === item.id &&
        prev.amountCents === item.amountCents &&
        prev.occurredAtIso === item.occurredAtIso &&
        prev.merchantName === item.merchantName &&
        prev.issuerRuc === item.issuerRuc &&
        prev.category === item.category &&
        prev.isInevitable === item.isInevitable &&
        prev.paymentMethod === item.paymentMethod &&
        prev.creditCardId === item.creditCardId &&
        prev.cardLastFourDigits === item.cardLastFourDigits &&
        prev.billingCycleHorizon === item.billingCycleHorizon &&
        prev.description === item.description &&
        prev.r2ReceiptKey === item.r2ReceiptKey &&
        prev.receiptImageDataUrl === item.receiptImageDataUrl
      );
    });

  if (isUnchanged) {
    return rawState;
  }

  return {
    ...state,
    expenses: nextExpenses,
  };
}

/**
 * Avanza el ciclo financiero al siguiente período (por ejemplo, cada día 21)
 * y descuenta de inmediato todos los gastos recurrentes activos en el Día 1 del nuevo ciclo.
 */
export function renewCycleToNextPeriod(
  rawState: FinancialCycleState,
  nextStartDateIso?: string,
  nextRenewalDateIso?: string
): FinancialCycleState {
  const syncedCurrent = synchronizeCycleRecurringExpenses(rawState);
  const currentKpis = computeSolTemporalKpis(syncedCurrent);

  const archivedCycleEntry = {
    cycleLabel: `${syncedCurrent.startDateIso.slice(0, 10)} → ${syncedCurrent.renewalDateIso.slice(0, 10)}`,
    spendingRateBp: syncedCurrent.spendingRateBp,
    totalCapCents: currentKpis.effectiveSpendingCapCents,
    totalSpentCents: currentKpis.totalSpentCents,
  };

  const effectiveNextStart = (
    nextStartDateIso ?? syncedCurrent.renewalDateIso
  ).slice(0, 10);
  const anchorDay = Number(effectiveNextStart.slice(8, 10)) || 1;
  const effectiveNextRenewal = (
    nextRenewalDateIso ??
    addOneMonthPreservingDayIso(effectiveNextStart, anchorDay)
  ).slice(0, 10);

  const todayIso = new Date().toISOString().slice(0, 10);
  const nextReferenceIso =
    todayIso >= effectiveNextStart && todayIso <= effectiveNextRenewal
      ? todayIso
      : effectiveNextStart;

  const renewedState: FinancialCycleState = {
    ...syncedCurrent,
    cycleId: `cycle-${effectiveNextStart}`,
    startDateIso: effectiveNextStart,
    referenceDateIso: nextReferenceIso,
    renewalDateIso: effectiveNextRenewal,
    windfallInflows: [],
    previousCyclesHistory: [
      archivedCycleEntry,
      ...(syncedCurrent.previousCyclesHistory ?? []),
    ].slice(0, 12),
  };

  return synchronizeCycleRecurringExpenses(renewedState);
}

export interface CategoryExpenseBreakdownItem {
  readonly category: ExpenseCategory;
  readonly label: string;
  readonly colorHex: string;
  readonly totalCents: number;
  readonly transactionCount: number;
  readonly shareOfTotalSpentPercent: number;
  readonly shareOfSpendingCapPercent: number;
  readonly dailyCompressionCents: number;
  readonly budgetHoursConsumed: number;
}

export interface MerchantExpenseBreakdownItem {
  readonly merchantKey: string;
  readonly merchantName: string;
  readonly issuerRuc: string | null;
  readonly primaryCategory: ExpenseCategory;
  readonly totalCents: number;
  readonly transactionCount: number;
  readonly shareOfTotalSpentPercent: number;
  readonly shareOfSpendingCapPercent: number;
  readonly dailyCompressionCents: number;
  readonly budgetHoursConsumed: number;
}

export interface ProductServiceImpactItem {
  readonly itemKey: string;
  readonly productOrServiceName: string;
  readonly merchantName: string;
  readonly issuerRuc: string | null;
  readonly category: ExpenseCategory;
  readonly quantityTotal: number;
  readonly totalCents: number;
  readonly shareOfTotalSpentPercent: number;
  readonly shareOfSpendingCapPercent: number;
  readonly dailyCompressionCents: number;
  readonly budgetHoursConsumed: number;
  readonly occurredAtIso: string;
  readonly source: ExpenseSource;
}

export interface ExpenseAnalyticsSummary {
  readonly totalSpentCents: number;
  readonly inevitableSpentCents: number;
  readonly controllableSpentCents: number;
  readonly recurringSpentCents: number;
  readonly variableSpentCents: number;
  readonly transactionCount: number;
  readonly controllableTransactionCount: number;
  readonly inevitableTransactionCount: number;
  readonly filteredExpenses: ReadonlyArray<ExpenseLedgerItem>;
  readonly byCategory: ReadonlyArray<CategoryExpenseBreakdownItem>;
  readonly byMerchant: ReadonlyArray<MerchantExpenseBreakdownItem>;
  readonly byProductOrService: ReadonlyArray<ProductServiceImpactItem>;
}

export function isDateWithinPeriod(
  dateIso: string,
  startDateIso: string,
  renewalDateIso: string
): boolean {
  const cleanDate = dateIso.slice(0, 10);
  const cleanStart = startDateIso.slice(0, 10);
  const cleanEnd = renewalDateIso.slice(0, 10);
  return cleanDate >= cleanStart && cleanDate <= cleanEnd;
}

export function filterExpensesByIssuePeriod(
  expenses: ReadonlyArray<ExpenseLedgerItem>,
  startDateIso: string,
  renewalDateIso: string
): ReadonlyArray<ExpenseLedgerItem> {
  return expenses.filter((exp) =>
    isDateWithinPeriod(exp.occurredAtIso, startDateIso, renewalDateIso)
  );
}

export function sortExpensesByIssueDateDesc(
  expenses: ReadonlyArray<ExpenseLedgerItem>
): ReadonlyArray<ExpenseLedgerItem> {
  return [...expenses].sort((a, b) =>
    b.occurredAtIso.slice(0, 10).localeCompare(a.occurredAtIso.slice(0, 10))
  );
}

export function isExpenseInevitableInState(
  exp: ExpenseLedgerItem,
  recurringExpenses?: ReadonlyArray<RecurringExpenseItem>
): boolean {
  if (typeof exp.isInevitable === 'boolean') {
    return exp.isInevitable;
  }
  if (exp.recurringRuleId && recurringExpenses) {
    const matched = recurringExpenses.find((r) => r.id === exp.recurringRuleId);
    if (matched) {
      return Boolean(matched.isInevitable);
    }
  }
  return false;
}

export function computeExpenseAnalytics(
  rawState: FinancialCycleState,
  snapshot: SolTemporalKpiSnapshot,
  periodRange?: { startDateIso: string; endDateIso: string } | 'ALL'
): ExpenseAnalyticsSummary {
  const state = synchronizeCycleRecurringExpenses(rawState);
  const filteredExpenses =
    periodRange === 'ALL'
      ? sortExpensesByIssueDateDesc(state.expenses)
      : sortExpensesByIssueDateDesc(
          filterExpensesByIssuePeriod(
            state.expenses,
            periodRange ? periodRange.startDateIso : state.startDateIso,
            periodRange ? periodRange.endDateIso : state.renewalDateIso
          )
        );

  const totalSpentCents = filteredExpenses.reduce(
    (acc, e) => acc + e.amountCents,
    0
  );
  const capCents = Math.max(1, snapshot.effectiveSpendingCapCents);
  const remainingDays = Math.max(1, snapshot.remainingDays);
  const baselineDailyCents = Math.max(1, snapshot.baselineDailyAllowanceCents);

  let recurringSpentCents = 0;
  let variableSpentCents = 0;
  let inevitableSpentCents = 0;
  let controllableSpentCents = 0;
  let inevitableTransactionCount = 0;
  let controllableTransactionCount = 0;

  const categoryMap = new Map<
    ExpenseCategory,
    { totalCents: number; count: number }
  >();

  const merchantMap = new Map<
    string,
    {
      merchantName: string;
      issuerRuc: string | null;
      primaryCategory: ExpenseCategory;
      totalCents: number;
      count: number;
    }
  >();

  const productMap = new Map<
    string,
    {
      productOrServiceName: string;
      merchantName: string;
      issuerRuc: string | null;
      category: ExpenseCategory;
      quantityTotal: number;
      totalCents: number;
      occurredAtIso: string;
      source: ExpenseSource;
    }
  >();

  for (const exp of filteredExpenses) {
    if (exp.source === 'RECURRING_RULE' || exp.recurringRuleId) {
      recurringSpentCents += exp.amountCents;
    } else {
      variableSpentCents += exp.amountCents;
    }

    const inevitable = isExpenseInevitableInState(exp, state.recurringExpenses);
    if (inevitable) {
      inevitableSpentCents += exp.amountCents;
      inevitableTransactionCount += 1;
      // REGLA DE NEGOCIO: Los gastos marcados como inevitables sí suman al gasto total del ciclo,
      // pero NO se consideran en los indicadores de control de gastos (categoría top, establecimiento top,
      // ni impacto por producto/servicio), pues solo interesa medir los gastos controlables.
      continue;
    }

    controllableSpentCents += exp.amountCents;
    controllableTransactionCount += 1;

    // 1. Por Categoría (Solo Gastos Controlables / No Inevitables)
    const prevCat = categoryMap.get(exp.category) ?? { totalCents: 0, count: 0 };
    categoryMap.set(exp.category, {
      totalCents: prevCat.totalCents + exp.amountCents,
      count: prevCat.count + 1,
    });

    // 2. Por Establecimiento (RUC o Razón Social — Solo Gastos Controlables)
    const normalizedRuc = exp.issuerRuc?.trim() || null;
    const merchantKey = normalizedRuc
      ? `RUC:${normalizedRuc}`
      : `NAME:${exp.merchantName.trim().toUpperCase()}`;
    const prevMerch = merchantMap.get(merchantKey);
    if (prevMerch) {
      merchantMap.set(merchantKey, {
        ...prevMerch,
        issuerRuc: prevMerch.issuerRuc ?? normalizedRuc,
        totalCents: prevMerch.totalCents + exp.amountCents,
        count: prevMerch.count + 1,
      });
    } else {
      merchantMap.set(merchantKey, {
        merchantName: exp.merchantName,
        issuerRuc: normalizedRuc,
        primaryCategory: exp.category,
        totalCents: exp.amountCents,
        count: 1,
      });
    }

    // 3. Por Producto o Servicio Consumido (Solo Gastos Controlables)
    const effectiveItems: ReadonlyArray<ExpenseLineItem> =
      exp.lineItems && exp.lineItems.length > 0
        ? exp.lineItems
        : [
            {
              description: exp.description.trim() || exp.merchantName,
              quantity: 1,
              totalCents: exp.amountCents,
            },
          ];

    for (const line of effectiveItems) {
      const cleanName = line.description.trim() || exp.merchantName;
      const prodKey = `${cleanName.toUpperCase()}::${merchantKey}`;
      const existingProd = productMap.get(prodKey);
      if (existingProd) {
        productMap.set(prodKey, {
          ...existingProd,
          quantityTotal: existingProd.quantityTotal + line.quantity,
          totalCents: existingProd.totalCents + line.totalCents,
        });
      } else {
        productMap.set(prodKey, {
          productOrServiceName: cleanName,
          merchantName: exp.merchantName,
          issuerRuc: normalizedRuc,
          category: exp.category,
          quantityTotal: line.quantity,
          totalCents: line.totalCents,
          occurredAtIso: exp.occurredAtIso,
          source: exp.source,
        });
      }
    }
  }

  const byCategory: CategoryExpenseBreakdownItem[] = Array.from(
    categoryMap.entries()
  )
    .map(([category, data]) => ({
      category,
      label: EXPENSE_CATEGORY_LABELS[category],
      colorHex: EXPENSE_CATEGORY_COLORS[category],
      totalCents: data.totalCents,
      transactionCount: data.count,
      shareOfTotalSpentPercent:
        controllableSpentCents > 0
          ? Number(((data.totalCents / controllableSpentCents) * 100).toFixed(1))
          : 0,
      shareOfSpendingCapPercent: Number(
        ((data.totalCents / capCents) * 100).toFixed(1)
      ),
      dailyCompressionCents: Math.round(data.totalCents / remainingDays),
      budgetHoursConsumed: Number(
        ((data.totalCents / baselineDailyCents) * 24).toFixed(1)
      ),
    }))
    .sort((a, b) => b.totalCents - a.totalCents);

  const byMerchant: MerchantExpenseBreakdownItem[] = Array.from(
    merchantMap.entries()
  )
    .map(([merchantKey, data]) => ({
      merchantKey,
      merchantName: data.merchantName,
      issuerRuc: data.issuerRuc,
      primaryCategory: data.primaryCategory,
      totalCents: data.totalCents,
      transactionCount: data.count,
      shareOfTotalSpentPercent:
        controllableSpentCents > 0
          ? Number(((data.totalCents / controllableSpentCents) * 100).toFixed(1))
          : 0,
      shareOfSpendingCapPercent: Number(
        ((data.totalCents / capCents) * 100).toFixed(1)
      ),
      dailyCompressionCents: Math.round(data.totalCents / remainingDays),
      budgetHoursConsumed: Number(
        ((data.totalCents / baselineDailyCents) * 24).toFixed(1)
      ),
    }))
    .sort((a, b) => b.totalCents - a.totalCents);

  const byProductOrService: ProductServiceImpactItem[] = Array.from(
    productMap.entries()
  )
    .map(([itemKey, data]) => ({
      itemKey,
      productOrServiceName: data.productOrServiceName,
      merchantName: data.merchantName,
      issuerRuc: data.issuerRuc,
      category: data.category,
      quantityTotal: data.quantityTotal,
      totalCents: data.totalCents,
      shareOfTotalSpentPercent:
        controllableSpentCents > 0
          ? Number(((data.totalCents / controllableSpentCents) * 100).toFixed(1))
          : 0,
      shareOfSpendingCapPercent: Number(
        ((data.totalCents / capCents) * 100).toFixed(1)
      ),
      dailyCompressionCents: Math.round(data.totalCents / remainingDays),
      budgetHoursConsumed: Number(
        ((data.totalCents / baselineDailyCents) * 24).toFixed(1)
      ),
      occurredAtIso: data.occurredAtIso,
      source: data.source,
    }))
    .sort((a, b) => b.totalCents - a.totalCents);

  return {
    totalSpentCents,
    inevitableSpentCents,
    controllableSpentCents,
    recurringSpentCents,
    variableSpentCents,
    transactionCount: filteredExpenses.length,
    controllableTransactionCount,
    inevitableTransactionCount,
    filteredExpenses,
    byCategory,
    byMerchant,
    byProductOrService,
  };
}

export function solesToCents(soles: number): number {
  return Math.round(soles * 100);
}

export function centsToSoles(cents: number): number {
  return Number((cents / 100).toFixed(2));
}

export function percentToBp(percent: number): number {
  const clamped = Math.max(0, Math.min(100, percent));
  return Math.round(clamped * 100);
}

export function bpToPercent(bp: number): number {
  return Number((bp / 100).toFixed(2));
}

export function formatSolesFromCents(cents: number, includePlusSign = false): string {
  const isNegative = cents < 0;
  const absCents = Math.abs(Math.round(cents));
  const integerPart = Math.floor(absCents / 100);
  const decimalPart = String(absCents % 100).padStart(2, '0');
  const formattedInt = integerPart.toLocaleString('es-PE');
  const sign = isNegative ? '-S/ ' : includePlusSign && cents > 0 ? '+S/ ' : 'S/ ';
  return `${sign}${formattedInt}.${decimalPart}`;
}

export function diffDaysBetweenIso(startIso: string, endIso: string): number {
  const startMs = Date.parse(`${startIso.slice(0, 10)}T00:00:00Z`);
  const endMs = Date.parse(`${endIso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return 30;
  }
  return Math.round((endMs - startMs) / (1000 * 60 * 60 * 24));
}

export function addDaysToIso(baseIso: string, daysToAdd: number): string {
  const baseMs = Date.parse(`${baseIso.slice(0, 10)}T00:00:00Z`);
  const targetMs = baseMs + Math.round(daysToAdd) * 86_400_000;
  return new Date(targetMs).toISOString().slice(0, 10);
}

export function computeWindfallContributionCents(
  amountCents: number,
  allocationMode: WindfallAllocationMode,
  cycleSpendingRateBp: number,
  customAllocationBp: number
): { capContributionCents: number; protectedSavingsCents: number } {
  if (allocationMode === 'RESERVE_ONLY') {
    return {
      capContributionCents: 0,
      protectedSavingsCents: amountCents,
    };
  }
  const effectiveBp =
    allocationMode === 'USE_CYCLE_RATE' ? cycleSpendingRateBp : customAllocationBp;
  const capContributionCents = Math.round((amountCents * effectiveBp) / 10000);
  return {
    capContributionCents,
    protectedSavingsCents: Math.max(0, amountCents - capContributionCents),
  };
}

export function computeSolTemporalKpis(
  rawState: FinancialCycleState
): SolTemporalKpiSnapshot {
  const state = synchronizeCycleRecurringExpenses(rawState);
  const totalCycleDays = Math.max(
    1,
    diffDaysBetweenIso(state.startDateIso, state.renewalDateIso)
  );
  const rawElapsed = diffDaysBetweenIso(state.startDateIso, state.referenceDateIso);
  const elapsedDays = Math.max(0, Math.min(totalCycleDays, rawElapsed));
  const remainingDays = Math.max(1, totalCycleDays - elapsedDays);

  const recurringInflowCents = state.recurringInflows.reduce(
    (acc, item) => acc + (item.isActive === false ? 0 : item.amountCents),
    0
  );
  const recurringCapContributionCents = Math.round(
    (recurringInflowCents * state.spendingRateBp) / 10000
  );

  let windfallInflowCents = 0;
  let windfallCapContributionCents = 0;

  for (const wf of state.windfallInflows) {
    windfallInflowCents += wf.amountCents;
    const { capContributionCents } = computeWindfallContributionCents(
      wf.amountCents,
      wf.allocationMode,
      state.spendingRateBp,
      wf.customAllocationBp
    );
    windfallCapContributionCents += capContributionCents;
  }

  const totalInflowCents = recurringInflowCents + windfallInflowCents;
  const effectiveSpendingCapCents =
    recurringCapContributionCents + windfallCapContributionCents;
  const protectedSavingsCents = Math.max(
    0,
    totalInflowCents - effectiveSpendingCapCents
  );

  const periodExpenses = filterExpensesByIssuePeriod(
    state.expenses,
    state.startDateIso,
    state.renewalDateIso
  ).filter((exp) => exp.billingCycleHorizon !== 'NEXT_CYCLE');
  const totalSpentCents = periodExpenses.reduce(
    (acc, exp) => acc + exp.amountCents,
    0
  );
  const remainingCapCents = effectiveSpendingCapCents - totalSpentCents;

  const remainingSpendingCapacityPercent =
    effectiveSpendingCapCents > 0
      ? Math.max(
          0,
          Math.min(
            100,
            Number(((remainingCapCents / effectiveSpendingCapCents) * 100).toFixed(2))
          )
        )
      : 0;

  const spentCapacityPercent =
    effectiveSpendingCapCents > 0
      ? Math.max(
          0,
          Number(((totalSpentCents / effectiveSpendingCapCents) * 100).toFixed(2))
        )
      : 0;

  // KPI-01: Cuota Diaria Inicial (D_0)
  const baselineDailyAllowanceCents = Math.round(
    effectiveSpendingCapCents / totalCycleDays
  );

  // KPI-02: Cuota Diaria Segura Actual (D_safe)
  const dynamicSafeDailyAllowanceCents = Math.max(
    0,
    Math.round(remainingCapCents / remainingDays)
  );

  // KPI-03: Factor de Compresión Futura por cada S/ 1.00 (100 céntimos) y S/ 100.00
  const dailyCompressionPerSolCents = Number((100 / remainingDays).toFixed(2));
  const dailyCompressionPer100SolesCents = Number((10000 / remainingDays).toFixed(2));

  // KPI-04: Equivalencia de S/ 1.00 en minutos de autonomía presupuestaria
  const minutesOfBudgetPerSol =
    baselineDailyAllowanceCents > 0
      ? Number(((100 / baselineDailyAllowanceCents) * 1440).toFixed(2))
      : 0;

  // KPI-05: Índice de Velocidad de Quema (BRVI)
  const effectiveElapsedForVelocity = Math.max(0.5, elapsedDays);
  const timeElapsedRatio = effectiveElapsedForVelocity / totalCycleDays;
  const capSpentRatio =
    effectiveSpendingCapCents > 0 ? totalSpentCents / effectiveSpendingCapCents : 1;
  const burnRateVelocityIndex = Number((capSpentRatio / timeElapsedRatio).toFixed(2));

  let burnVelocityStatus: BurnVelocityStatus = 'OPTIMAL';
  if (totalSpentCents > effectiveSpendingCapCents || burnRateVelocityIndex > 1.25) {
    burnVelocityStatus = 'CRITICAL';
  } else if (burnRateVelocityIndex > 1.05) {
    burnVelocityStatus = 'WARNING';
  } else if (burnRateVelocityIndex > 0.95) {
    burnVelocityStatus = 'ON_TRACK';
  }

  // KPI-06: Autonomía Real Restante (Runway en días)
  const averageDailySpendCents = totalSpentCents / Math.max(1, elapsedDays);
  const realRunwayDays =
    remainingCapCents <= 0
      ? 0
      : averageDailySpendCents <= 0
        ? remainingDays
        : Number((remainingCapCents / averageDailySpendCents).toFixed(1));

  const projectedExhaustionDateIso =
    remainingCapCents <= 0
      ? state.referenceDateIso
      : addDaysToIso(state.referenceDateIso, Math.floor(realRunwayDays));

  // KPI-07: Brecha de Superávit/Déficit de Ritmo Acumulado (B_t)
  const idealSpentToDateCents = baselineDailyAllowanceCents * elapsedDays;
  const pacingBufferCents = idealSpentToDateCents - totalSpentCents;

  // Serie temporal diaria para el gráfico SVG
  const dailyTrajectory: Array<{
    readonly dayNumber: number;
    readonly dateIso: string;
    readonly idealCumulativeCapCents: number;
    readonly actualCumulativeSpentCents: number | null;
    readonly dynamicSafeAllowanceOnDayCents: number;
  }> = [];

  for (let day = 1; day <= totalCycleDays; day += 1) {
    const dateIso = addDaysToIso(state.startDateIso, day);
    const idealCumulativeCapCents = Math.min(
      effectiveSpendingCapCents,
      baselineDailyAllowanceCents * day
    );

    if (day <= Math.max(1, elapsedDays)) {
      const spentUpToDay = periodExpenses
        .filter((e) => e.occurredAtIso.slice(0, 10) <= dateIso)
        .reduce((acc, e) => acc + e.amountCents, 0);
      const remDaysAtPoint = Math.max(1, totalCycleDays - day);
      const remCapAtPoint = Math.max(0, effectiveSpendingCapCents - spentUpToDay);
      dailyTrajectory.push({
        dayNumber: day,
        dateIso,
        idealCumulativeCapCents,
        actualCumulativeSpentCents: spentUpToDay,
        dynamicSafeAllowanceOnDayCents: Math.round(remCapAtPoint / remDaysAtPoint),
      });
    } else {
      dailyTrajectory.push({
        dayNumber: day,
        dateIso,
        idealCumulativeCapCents,
        actualCumulativeSpentCents: null,
        dynamicSafeAllowanceOnDayCents: dynamicSafeDailyAllowanceCents,
      });
    }
  }

  return {
    cycleId: state.cycleId,
    startDateIso: state.startDateIso,
    renewalDateIso: state.renewalDateIso,
    totalCycleDays,
    elapsedDays,
    remainingDays,
    recurringInflowCents,
    windfallInflowCents,
    totalInflowCents,
    spendingRateBp: state.spendingRateBp,
    spendingRatePercent: bpToPercent(state.spendingRateBp),
    keepRateConstantAcrossPeriods: state.keepRateConstantAcrossPeriods,
    effectiveSpendingCapCents,
    totalSpentCents,
    remainingCapCents,
    protectedSavingsCents,
    remainingSpendingCapacityPercent,
    spentCapacityPercent,
    baselineDailyAllowanceCents,
    dynamicSafeDailyAllowanceCents,
    dailyCompressionPerSolCents,
    dailyCompressionPer100SolesCents,
    minutesOfBudgetPerSol,
    burnRateVelocityIndex,
    burnVelocityStatus,
    realRunwayDays,
    projectedExhaustionDateIso,
    pacingBufferCents,
    dailyTrajectory,
  };
}

export function simulateExpenseImpact(
  snapshot: SolTemporalKpiSnapshot,
  proposedExpenseSoles: number
): ExpenseImpactSimulation {
  const proposedExpenseCents = Math.max(0, solesToCents(proposedExpenseSoles));
  const remainingCapAfterExpenseCents =
    snapshot.remainingCapCents - proposedExpenseCents;
  const willExceedCycleCap = remainingCapAfterExpenseCents < 0;
  const newRemainingSpendingCapacityPercent =
    snapshot.effectiveSpendingCapCents > 0
      ? Math.max(
          0,
          Math.min(
            100,
            Number(
              ((remainingCapAfterExpenseCents / snapshot.effectiveSpendingCapCents) * 100).toFixed(2)
            )
          )
        )
      : 0;
  const capacityPercentConsumedByExpense =
    snapshot.effectiveSpendingCapCents > 0
      ? Number(((proposedExpenseCents / snapshot.effectiveSpendingCapCents) * 100).toFixed(2))
      : 0;

  const newSafeDailyAllowanceCents = Math.max(
    0,
    Math.round(remainingCapAfterExpenseCents / snapshot.remainingDays)
  );
  const dailyAllowanceReductionCents = Math.max(
    0,
    snapshot.dynamicSafeDailyAllowanceCents - newSafeDailyAllowanceCents
  );

  const budgetTimeCostHours =
    snapshot.baselineDailyAllowanceCents > 0
      ? Number(
          ((proposedExpenseCents / snapshot.baselineDailyAllowanceCents) * 24).toFixed(2)
        )
      : 0;
  const budgetTimeCostDays = Number((budgetTimeCostHours / 24).toFixed(2));

  const narrativeSummary = willExceedCycleCap
    ? `⚠️ Este gasto de ${formatSolesFromCents(proposedExpenseCents)} excede tu Tope de Gasto disponible por ${formatSolesFromCents(Math.abs(remainingCapAfterExpenseCents))} antes de la renovación (${snapshot.renewalDateIso}).`
    : `Gastar ${formatSolesFromCents(proposedExpenseCents)} hoy comprime tu Cuota Diaria Segura en ${formatSolesFromCents(dailyAllowanceReductionCents)}/día durante los ${snapshot.remainingDays} días restantes y consume ${budgetTimeCostHours} horas de vida presupuestaria.`;

  return {
    proposedExpenseCents,
    proposedExpenseSoles: centsToSoles(proposedExpenseCents),
    currentSafeDailyAllowanceCents: snapshot.dynamicSafeDailyAllowanceCents,
    newSafeDailyAllowanceCents,
    dailyAllowanceReductionCents,
    budgetTimeCostHours,
    budgetTimeCostDays,
    willExceedCycleCap,
    remainingCapAfterExpenseCents,
    newRemainingSpendingCapacityPercent,
    capacityPercentConsumedByExpense,
    narrativeSummary,
  };
}

export interface CreditCardCycleSummaryItem {
  readonly card: CreditCardProfileDto;
  readonly currentCycleBillingCloseDateIso: string;
  readonly currentCyclePaymentDueDateIso: string;
  readonly isBillingOpenToday: boolean;
  readonly daysUntilBillingClose: number;
  readonly currentCycleSpentCents: number;
  readonly currentCycleRecurringOnCardCents: number;
  readonly nextCycleSpentCents: number;
  readonly effectiveCardLimitCents: number;
  readonly remainingCardCapCents: number;
  readonly usedPercentOfCardLimit: number;
  readonly hasReachedLimitInCurrentCycle: boolean;
}

export interface SmartCardLimitAlertItem {
  readonly cardId: string;
  readonly cardAlias: string;
  readonly spentCents: number;
  readonly limitCents: number;
  readonly alternativeOpenCardId: string | null;
  readonly alternativeOpenCardAlias: string | null;
  readonly alertMessage: string;
}

export interface SmartCardAdvisorRecommendation {
  readonly hasCardsConfigured: boolean;
  readonly recommendedCardId: string | null;
  readonly recommendedCardAlias: string | null;
  readonly recommendedTargetHorizon: BillingCycleHorizon;
  readonly headline: string;
  readonly detailMessage: string;
  readonly tacticalPostCloseCardId: string | null;
  readonly tacticalPostCloseCardAlias: string | null;
  readonly tacticalStrategyNote: string | null;
  readonly cardsAtLimitAlerts: ReadonlyArray<SmartCardLimitAlertItem>;
}

export interface MultiCycleCardSummary {
  readonly cards: ReadonlyArray<CreditCardCycleSummaryItem>;
  readonly currentCycleCardSpentCents: number;
  readonly currentCycleDirectSpentCents: number;
  readonly nextCycleCommittedCents: number;
  readonly nextCycleShareOfCapPercent: number;
  readonly advisor: SmartCardAdvisorRecommendation;
}

export function computeMultiCycleCardSummary(
  rawState: FinancialCycleState,
  snapshot: SolTemporalKpiSnapshot
): MultiCycleCardSummary {
  const state = synchronizeCycleRecurringExpenses(rawState);
  const activeCards = (state.creditCards ?? [])
    .filter((c) => c.isActive !== false)
    .slice(0, MAX_USER_CREDIT_CARDS);

  const refDateIso = state.referenceDateIso.slice(0, 10);
  const allPeriodExpenses = filterExpensesByIssuePeriod(
    state.expenses,
    state.startDateIso,
    state.renewalDateIso
  );

  const cards: CreditCardCycleSummaryItem[] = activeCards.map((card) => {
    const currentCycleBillingCloseDateIso = resolveCardBillingCloseDateForCycle(
      card.billingCloseDayOfMonth,
      state.startDateIso,
      state.renewalDateIso
    );
    const currentCyclePaymentDueDateIso = resolveCardPaymentDueDateForCycle(
      card.paymentDueDayOfMonth,
      currentCycleBillingCloseDateIso
    );
    const isBillingOpenToday = refDateIso <= currentCycleBillingCloseDateIso;
    const daysUntilBillingClose = diffDaysBetweenIso(
      refDateIso,
      currentCycleBillingCloseDateIso
    );

    const cardExpensesInRange = allPeriodExpenses.filter(
      (exp) => exp.creditCardId === card.id
    );

    let currentCycleSpentCents = 0;
    let currentCycleRecurringOnCardCents = 0;
    let nextCycleSpentCents = 0;

    for (const exp of cardExpensesInRange) {
      if (exp.billingCycleHorizon === 'NEXT_CYCLE') {
        nextCycleSpentCents += exp.amountCents;
      } else {
        currentCycleSpentCents += exp.amountCents;
        if (exp.source === 'RECURRING_RULE' || exp.recurringRuleId) {
          currentCycleRecurringOnCardCents += exp.amountCents;
        }
      }
    }

    const effectiveCardLimitCents =
      typeof card.monthlyLimitCents === 'number' && card.monthlyLimitCents > 0
        ? card.monthlyLimitCents
        : Math.max(0, snapshot.effectiveSpendingCapCents);

    const remainingCardCapCents =
      effectiveCardLimitCents - currentCycleSpentCents;
    const usedPercentOfCardLimit =
      effectiveCardLimitCents > 0
        ? Number(
            ((currentCycleSpentCents / effectiveCardLimitCents) * 100).toFixed(1)
          )
        : 0;
    const hasReachedLimitInCurrentCycle =
      effectiveCardLimitCents > 0 &&
      currentCycleSpentCents >= effectiveCardLimitCents;

    return {
      card,
      currentCycleBillingCloseDateIso,
      currentCyclePaymentDueDateIso,
      isBillingOpenToday,
      daysUntilBillingClose,
      currentCycleSpentCents,
      currentCycleRecurringOnCardCents,
      nextCycleSpentCents,
      effectiveCardLimitCents,
      remainingCardCapCents,
      usedPercentOfCardLimit,
      hasReachedLimitInCurrentCycle,
    };
  });

  const currentCycleCardSpentCents = allPeriodExpenses
    .filter(
      (exp) =>
        exp.billingCycleHorizon !== 'NEXT_CYCLE' &&
        (exp.paymentMethod === 'CREDIT_CARD' || Boolean(exp.creditCardId))
    )
    .reduce((sum, exp) => sum + exp.amountCents, 0);

  const currentCycleDirectSpentCents = allPeriodExpenses
    .filter(
      (exp) =>
        exp.billingCycleHorizon !== 'NEXT_CYCLE' &&
        exp.paymentMethod !== 'CREDIT_CARD' &&
        !exp.creditCardId
    )
    .reduce((sum, exp) => sum + exp.amountCents, 0);

  const nextCycleCommittedCents = state.expenses
    .filter((exp) => exp.billingCycleHorizon === 'NEXT_CYCLE')
    .reduce((sum, exp) => sum + exp.amountCents, 0);

  const nextCycleShareOfCapPercent =
    snapshot.effectiveSpendingCapCents > 0
      ? Number(
          (
            (nextCycleCommittedCents / snapshot.effectiveSpendingCapCents) *
            100
          ).toFixed(1)
        )
      : 0;

  if (cards.length === 0) {
    return {
      cards,
      currentCycleCardSpentCents,
      currentCycleDirectSpentCents,
      nextCycleCommittedCents,
      nextCycleShareOfCapPercent,
      advisor: {
        hasCardsConfigured: false,
        recommendedCardId: null,
        recommendedCardAlias: null,
        recommendedTargetHorizon: 'CURRENT_CYCLE',
        headline: 'Registra hasta 3 tarjetas para activar el Asesor Inteligente',
        detailMessage:
          'Configura la Fecha de Facturación y Fecha de Pago de tus tarjetas para saber exactamente hasta qué día puedes gastar y qué compras pasan al Próximo Ciclo.',
        tacticalPostCloseCardId: null,
        tacticalPostCloseCardAlias: null,
        tacticalStrategyNote: null,
        cardsAtLimitAlerts: [],
      },
    };
  }

  // Tarjetas aún con facturación abierta hoy y con saldo disponible en este ciclo
  const openAvailableCards = cards
    .filter(
      (c) =>
        c.isBillingOpenToday &&
        !c.hasReachedLimitInCurrentCycle &&
        c.remainingCardCapCents > 0
    )
    .sort((a, b) => b.remainingCardCapCents - a.remainingCardCapCents);

  // Tarjetas cuya facturación ya cerró en este período (sus compras van al Próximo Ciclo)
  const postCloseCards = cards.filter((c) => !c.isBillingOpenToday);

  // Alertas de tarjetas que llegaron a su tope en el presente ciclo (Regla 2.1)
  const cardsAtLimitAlerts: SmartCardLimitAlertItem[] = cards
    .filter((c) => c.hasReachedLimitInCurrentCycle)
    .map((c) => {
      const alternative = openAvailableCards.find(
        (alt) => alt.card.id !== c.card.id
      );
      const alertMessage = alternative
        ? `⚠️ Llegaste al tope de gastos con ${c.card.alias} (${formatSolesFromCents(c.currentCycleSpentCents)} / ${formatSolesFromCents(c.effectiveCardLimitCents)}) en el presente ciclo. Realiza tu próximo gasto con ${alternative.card.alias} (puedes gastar hasta su facturación el ${alternative.currentCycleBillingCloseDateIso}).`
        : postCloseCards.length > 0
          ? `⚠️ Llegaste al tope de gastos con ${c.card.alias} (${formatSolesFromCents(c.currentCycleSpentCents)}) en este ciclo. Tus tarjetas abiertas agotaron su tope; si es urgente, usa ${postCloseCards[0]!.card.alias} (corte cerrado: pasará al Próximo Ciclo).`
          : `⚠️ Llegaste al tope de gastos con ${c.card.alias} (${formatSolesFromCents(c.currentCycleSpentCents)}) en el presente ciclo y no tienes otra tarjeta con facturación abierta disponible.`;
      return {
        cardId: c.card.id,
        cardAlias: c.card.alias,
        spentCents: c.currentCycleSpentCents,
        limitCents: c.effectiveCardLimitCents,
        alternativeOpenCardId: alternative?.card.id ?? null,
        alternativeOpenCardAlias: alternative?.card.alias ?? null,
        alertMessage,
      };
    });

  const bestOpenCard = openAvailableCards[0] ?? null;
  const bestPostCloseCard = postCloseCards[0] ?? null;
  const hasCycleAvailableCap = snapshot.remainingCapCents > 0;

  // Regla 1.2: Si todavía tiene disponible para el ciclo actual y hay una tarjeta que aún no cerró facturación,
  // recomendar esa tarjeta para el ciclo actual, y si además existe una tarjeta con facturación ya cerrada,
  // incluir el mensaje estratégico corto sobre diferir un gasto importante u oportunidad de inversión.
  if (hasCycleAvailableCap && bestOpenCard) {
    const tacticalNote = bestPostCloseCard
      ? `💡 Estrategia táctica: ${bestPostCloseCard.card.alias} ya cerró facturación (${bestPostCloseCard.currentCycleBillingCloseDateIso}). Úsala hoy si necesitas cubrir un gasto importante u oportunidad de inversión pasando el cobro al Próximo Ciclo.`
      : null;

    return {
      cards,
      currentCycleCardSpentCents,
      currentCycleDirectSpentCents,
      nextCycleCommittedCents,
      nextCycleShareOfCapPercent,
      advisor: {
        hasCardsConfigured: true,
        recommendedCardId: bestOpenCard.card.id,
        recommendedCardAlias: bestOpenCard.card.alias,
        recommendedTargetHorizon: 'CURRENT_CYCLE',
        headline: `Usa hoy ${bestOpenCard.card.alias} (facturación abierta hasta el ${bestOpenCard.currentCycleBillingCloseDateIso})`,
        detailMessage: `Aún tienes ${formatSolesFromCents(bestOpenCard.remainingCardCapCents)} disponibles en ${bestOpenCard.card.alias} para el Ciclo Actual (puedes gastar hasta el ${bestOpenCard.currentCycleBillingCloseDateIso}; fecha de pago: ${bestOpenCard.currentCyclePaymentDueDateIso}).`,
        tacticalPostCloseCardId: bestPostCloseCard?.card.id ?? null,
        tacticalPostCloseCardAlias: bestPostCloseCard?.card.alias ?? null,
        tacticalStrategyNote: tacticalNote,
        cardsAtLimitAlerts,
      },
    };
  }

  // Si ya hay una tarjeta cuya fecha de facturación cerró y no quedan tarjetas abiertas con saldo en este ciclo
  if (bestPostCloseCard) {
    return {
      cards,
      currentCycleCardSpentCents,
      currentCycleDirectSpentCents,
      nextCycleCommittedCents,
      nextCycleShareOfCapPercent,
      advisor: {
        hasCardsConfigured: true,
        recommendedCardId: bestPostCloseCard.card.id,
        recommendedCardAlias: bestPostCloseCard.card.alias,
        recommendedTargetHorizon: 'NEXT_CYCLE',
        headline: `Usa hoy ${bestPostCloseCard.card.alias}: su facturación cerró el ${bestPostCloseCard.currentCycleBillingCloseDateIso}`,
        detailMessage: `Las compras con ${bestPostCloseCard.card.alias} desde hoy pasan automáticamente al Monitor del Próximo Ciclo sin descontar tu disponible de este mes.`,
        tacticalPostCloseCardId: bestPostCloseCard.card.id,
        tacticalPostCloseCardAlias: bestPostCloseCard.card.alias,
        tacticalStrategyNote:
          '💡 Ideal para cubrir un gasto importante u oportunidad de inversión difiriendo el pago al siguiente ciclo.',
        cardsAtLimitAlerts,
      },
    };
  }

  const fallbackCard = cards[0]!;
  return {
    cards,
    currentCycleCardSpentCents,
    currentCycleDirectSpentCents,
    nextCycleCommittedCents,
    nextCycleShareOfCapPercent,
    advisor: {
      hasCardsConfigured: true,
      recommendedCardId: fallbackCard.card.id,
      recommendedCardAlias: fallbackCard.card.alias,
      recommendedTargetHorizon: 'CURRENT_CYCLE',
      headline: `Cuidado: alcanzaste el tope del Ciclo Actual en tus tarjetas`,
      detailMessage: `Evita nuevos gastos con tarjeta hasta que cierre la facturación (${fallbackCard.currentCycleBillingCloseDateIso}) o se renueve tu ingreso (${snapshot.renewalDateIso}).`,
      tacticalPostCloseCardId: null,
      tacticalPostCloseCardAlias: null,
      tacticalStrategyNote: null,
      cardsAtLimitAlerts,
    },
  };
}

export interface PeruvianReceiptTemplate {
  readonly id: string;
  readonly badge: string;
  readonly title: string;
  readonly subtitle: string;
  readonly extraction: ReceiptExtractionResult;
}

export const PERUVIAN_RECEIPT_TEMPLATES: ReadonlyArray<PeruvianReceiptTemplate> = [];

export const INITIAL_RECURRING_EXPENSES: ReadonlyArray<RecurringExpenseItem> = [];

export const INITIAL_CYCLE_STATE: FinancialCycleState = {
  cycleId: 'cyc-2026-10-active',
  startDateIso: '2026-10-01',
  referenceDateIso: '2026-10-26',
  renewalDateIso: '2026-10-31',
  spendingRateBp: 7000, // 70.00%
  keepRateConstantAcrossPeriods: true,
  creditCards: [],
  recurringInflows: [],
  recurringExpenses: [],
  windfallInflows: [],
  expenses: [],
  previousCyclesHistory: [],
};

export const INITIAL_ENTITLEMENT_STATE: EntitlementState = {
  mode: 'SOFT_LAUNCH_ALLOWLIST',
  maxPilotEmails: 2,
  pilotAllowlist: [
    {
      email: 'alexdel2022@gmail.com',
      roleLabel: 'Correo Piloto #1 (Titular Principal)',
      verifiedViaResend: true,
      lastOtpCode: '849201',
      verifiedAtIso: '2026-10-01T08:15:00Z',
    },
    {
      email: 'mari26roque@gmail.com',
      roleLabel: 'Correo Piloto #2 (Co-Piloto Autorizado)',
      verifiedViaResend: true,
      lastOtpCode: '391044',
      verifiedAtIso: '2026-10-01T09:02:00Z',
    },
  ],
  trialDaysDuration: 14,
  monthlyPriceSoles: 19.9,
  activeUserEmail: 'alexdel2022@gmail.com',
  activeSubscriptionStatus: 'PILOT_UNLIMITED',
};

export const INITIAL_DEVELOPER_KEYS: ReadonlyArray<DeveloperApiKeyItem> = [
  {
    id: 'key-prod-01',
    label: 'Webhook Automatización Yape / Apple Shortcuts',
    keyPrefix: 'eco_live_9f8a7b6c',
    fullTokenPreview: 'eco_live_9f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c',
    scopes: ['kpi:read', 'expenses:write', 'receipts:scan'],
    createdAtIso: '2026-10-02T10:30:00Z',
    lastUsedAtIso: '2026-10-14T21:14:00Z',
  },
];
